export interface AuthTokenService {
    getAuthToken(): string | null;

    setAuthToken(token: string): void;

    clearAuthToken(): void;

    isTokenExpired(): boolean;

    refreshToken(): Promise<string>;
}

function base64UrlDecodeToString(input: string): string {
    const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');

    if (typeof atob === 'function') {
        return atob(padded);
    }

    // Node.js fallback (SSR/tests). Buffer is not available in browsers.
    // eslint-disable-next-line no-undef
    return Buffer.from(padded, 'base64').toString('utf8');
}

function getJwtExpiryMs(token: string): number | null {
    try {
        const parts = token.split('.');
        if (parts.length < 2) return null;

        const payloadJson = base64UrlDecodeToString(parts[1]!);
        const payload = JSON.parse(payloadJson) as { exp?: number };
        if (typeof payload.exp !== 'number') return null;

        return payload.exp * 1000;
    } catch {
        return null;
    }
}

export class FirebaseAuthTokenService implements AuthTokenService {
    private currentToken: string | null = null;
    private tokenExpiry: number | null = null;

    getAuthToken(): string | null {
        if (this.isTokenExpired()) {
            return null;
        }
        return this.currentToken;
    }

    setAuthToken(token: string): void {
        this.currentToken = token;
        // Decode JWT to get expiry (basic implementation)
        const expiry = getJwtExpiryMs(token);
        this.tokenExpiry = expiry ?? (Date.now() + 60 * 60 * 1000);
    }

    clearAuthToken(): void {
        this.currentToken = null;
        this.tokenExpiry = null;
    }

    isTokenExpired(): boolean {
        if (!this.tokenExpiry) {
            return true;
        }
        // Consider token expired if it expires within the next 5 minutes
        return Date.now() >= (this.tokenExpiry - 5 * 60 * 1000);
    }

    async refreshToken(): Promise<string> {
        if (typeof window === 'undefined') {
            this.clearAuthToken();
            throw new Error('Token refresh is only available in the browser');
        }

        const response = await fetch('/api/auth/refresh', {method: 'POST'});
        if (!response.ok) {
            this.clearAuthToken();
            throw new Error('Failed to refresh authentication token');
        }

        const data = (await response.json().catch(() => ({}))) as {
            access_token?: string;
            access?: string;
            refresh_token?: string;
            refresh?: string;
        };

        const accessToken =
            typeof data.access_token === 'string'
                ? data.access_token
                : typeof data.access === 'string'
                    ? data.access
                    : '';

        if (!accessToken) {
            this.clearAuthToken();
            throw new Error('Failed to refresh authentication token');
        }

        this.setAuthToken(accessToken);
        return accessToken;
    }
}

// Local storage implementation for browser environments
export class LocalStorageAuthTokenService implements AuthTokenService {
    private readonly TOKEN_KEY = 'motito_auth_token';
    private readonly EXPIRY_KEY = 'motito_auth_expiry';

    getAuthToken(): string | null {
        if (typeof window === 'undefined') return null;

        if (this.isTokenExpired()) {
            this.clearAuthToken();
            return null;
        }

        return localStorage.getItem(this.TOKEN_KEY);
    }

    setAuthToken(token: string): void {
        if (typeof window === 'undefined') return;

        localStorage.setItem(this.TOKEN_KEY, token);

        // Decode JWT to get expiry
        const expiry = getJwtExpiryMs(token) ?? (Date.now() + 60 * 60 * 1000);
        localStorage.setItem(this.EXPIRY_KEY, expiry.toString());
    }

    clearAuthToken(): void {
        if (typeof window === 'undefined') return;

        localStorage.removeItem(this.TOKEN_KEY);
        localStorage.removeItem(this.EXPIRY_KEY);
    }

    isTokenExpired(): boolean {
        if (typeof window === 'undefined') return true;

        const expiryStr = localStorage.getItem(this.EXPIRY_KEY);
        if (!expiryStr) return true;

        const expiry = parseInt(expiryStr, 10);
        // Consider token expired if it expires within the next 5 minutes
        return Date.now() >= (expiry - 5 * 60 * 1000);
    }

    async refreshToken(): Promise<string> {
        if (typeof window === 'undefined') {
            this.clearAuthToken();
            throw new Error('Token refresh is only available in the browser');
        }

        const response = await fetch('/api/auth/refresh', {method: 'POST'});
        if (!response.ok) {
            this.clearAuthToken();
            throw new Error('Failed to refresh authentication token');
        }

        const data = (await response.json().catch(() => ({}))) as {
            access_token?: string;
            access?: string;
            refresh_token?: string;
            refresh?: string;
        };

        const accessToken =
            typeof data.access_token === 'string'
                ? data.access_token
                : typeof data.access === 'string'
                    ? data.access
                    : '';

        const refreshToken =
            typeof data.refresh_token === 'string'
                ? data.refresh_token
                : typeof data.refresh === 'string'
                    ? data.refresh
                    : '';

        if (!accessToken) {
            this.clearAuthToken();
            throw new Error('Failed to refresh authentication token');
        }

        this.setAuthToken(accessToken);

        // Best-effort: keep AuthStore's persisted refresh token in sync when present.
        if (refreshToken) {
            try {
                const stored = localStorage.getItem('auth_tokens');
                const parsed = stored ? (JSON.parse(stored) as {accessToken?: string; refreshToken?: string}) : null;
                localStorage.setItem(
                    'auth_tokens',
                    JSON.stringify({
                        accessToken,
                        refreshToken: refreshToken || parsed?.refreshToken || '',
                    }),
                );
            } catch {
                // Ignore storage failures
            }
        }

        return accessToken;
    }
}
